import React, { useState } from 'react';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import classes from './EditTimesheetFormFullMonth.module.scss';
import colors from '../../../../../../scss/colors';
import SearchLocation from '../../../../../common/SearchLocation/SearchLocation';
import StyledLocationContainer from '../../EditTimesheetModal/components/StyledLocationContainer';
import { qtyAllowedForComment, qtyToShowComment } from '../../../../../../utils/constants';
import NotificationModal from '../../../../../common/ModalWindows/NotificationModal';
import MonthCalendar from '../../../../../common/MonthCalendar/MonthCalendar';
import { isValidByRateType } from '../../../../../../utils/helpers';
import { rateEnum } from '../../../../../../utils/enums';

const StyledCategoryItem = styled('div')(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '34px',
  border: `0.5px solid ${colors.MEDIUM_GREY}`,
  padding: '10px',
  backgroundColor: selected ? colors.NAVY_BLUE : 'transparent',
  whiteSpace: 'nowrap',
  color: selected ? colors.WHITE : colors.MEDIUM_GREY,
  fontWeight: 600,
  fontSize: '16px'
}));

const textFieldStyles = {
  width: '100%',
  backgroundColor: colors.WHITE,
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${colors.LIGHT_GREY}`,
    borderRadius: '10px'
  },
  color: colors.BLACK_TXT,
  fontSize: '14px',
  fontWeight: '325',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
};

const EditTimesheetFormFullMonth = ({
  isComplexWorktype = false,
  formData,
  allowedWorkTypes,
  rateType,
  choosedMonth,
  handleWhenCleared = () => {}
}) => {
  const { t } = useTranslation();

  const [openRateError, setOpenRateError] = useState('');

  const changeHandler = (date, newValue) => {
    if (!isValidByRateType(newValue, rateType)) {
      setOpenRateError(
        rateType === rateEnum.HOURLY
          ? t('dashboard.timesheet.validations.max24h')
          : t('dashboard.timesheet.validations.max1day')
      );
    }

    formData.setFieldValue(
      'TimesheetHoursByDates',
      formData.values.TimesheetHoursByDates.map((value) => {
        return dayjs(value.Day.toString()).isSame(date)
          ? {
              Day: value.Day,
              TotalHrs: isValidByRateType(newValue, rateType) ? Number(newValue) : value.TotalHrs
            }
          : value;
      })
    );
  };

  return (
    <>
      <div className={classes.auxContainer}>
        <p>{t('dashboard.timesheet.table.worktype')}*</p>
        <div className={classes.categoryContainer}>
          {allowedWorkTypes.map((item) =>
            isComplexWorktype ? (
              <StyledCategoryItem
                key={item.Id}
                selected={item.Id === formData.values.WorkType.Id}
                onClick={() => formData.setFieldValue('WorkType', item)}
              >
                {item.Name}
              </StyledCategoryItem>
            ) : (
              <StyledCategoryItem
                key={item.key}
                selected={item.value === formData.values.WorkTypeId}
                onClick={() => formData.setFieldValue('WorkTypeId', item.key)}
              >
                {item.label}
              </StyledCategoryItem>
            )
          )}
        </div>
      </div>
      <div className={classes.separator} />
      <div className={classes.auxContainer}>
        <p>{t('common.forms.simplelocation')}*</p>
        <StyledLocationContainer>
          <SearchLocation
            value={formData.values.Location}
            changeHandler={(value) => formData.setFieldValue('Location', value)}
            handleWhenCleared={handleWhenCleared}
            withClear={false}
            placeholder={`${t('dashboard.timesheet.table.location')}`}
          />
        </StyledLocationContainer>
      </div>
      <div className={classes.separator} />
      <div className={classes.auxContainer}>
        <p>{t('common.forms.hours')}*</p>
        <MonthCalendar
          data={formData.values.TimesheetHoursByDates}
          dateProperty="Day"
          valueProperty="TotalHrs"
          monthIndex={choosedMonth}
          changeHandler={changeHandler}
        />
      </div>
      <div className={classes.separator} />
      <div className={classes.auxContainer}>
        <p>{t('common.forms.comment')}</p>
        <TextField
          multiline
          minRows={2}
          maxRows={3}
          placeholder={t('dashboard.timesheet.table.comment')}
          value={formData.values.Comment}
          onChange={(e) => {
            if (e.target.value.length <= qtyAllowedForComment) formData.setFieldValue('Comment', e.target.value);
          }}
          sx={textFieldStyles}
        />
        {formData.values.Comment.length >= qtyToShowComment && (
          <p className={classes.counter}>{`${formData.values.Comment.length}/${qtyAllowedForComment}`}</p>
        )}
      </div>
      {openRateError && (
        <NotificationModal open={openRateError} onClose={() => setOpenRateError('')}>
          <p className={classes.errorMessage}>{openRateError}</p>
        </NotificationModal>
      )}
    </>
  );
};

export default EditTimesheetFormFullMonth;
